/**
 * Enhanced Embed Detection and URL Normalization Utilities
 * Consolidated from multiple files for better functionality
 */

export interface DetectedAsset {
  platform: string;
  url: string;
  embedCode?: string;
  contentType?: 'post' | 'reel' | 'video' | 'story' | 'shorts';
}

export interface EmbedMetadata {
  type: 'youtube' | 'tiktok' | 'instagram' | 'twitter' | 'facebook' | 'linkedin' | 'unknown';
  id: string | null;
  embedUrl: string | null;
  originalUrl: string;
  title?: string;
  contentType?: 'post' | 'reel' | 'video' | 'story' | 'shorts';
}

/**
 * Normalize a URL by removing tracking parameters and ensuring proper format
 * 
 * Removes common tracking parameters (utm_*, fbclid, gclid, etc.) from URLs
 * to create clean, canonical URLs for storage and comparison.
 * 
 * @param url URL string to normalize
 * @returns Normalized URL string, or original if parsing fails
 * 
 * @example
 * normalizeUrl('https://example.com?utm_source=google&id=123')
 * // 'https://example.com/?id=123'
 */
export function normalizeUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    
    // Remove common tracking parameters
    const trackingParams = [
      'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
      'fbclid', 'gclid', 'ref', 'source', 'campaign'
    ];
    
    trackingParams.forEach(param => {
      urlObj.searchParams.delete(param);
    });
    
    return urlObj.toString();
  } catch {
    return url;
  }
}

/**
 * Extract canonical URL from embed code or normalize existing URL
 * 
 * If input is already a URL, normalizes it. If input is embed code,
 * extracts the URL from the embed HTML.
 * 
 * @param input URL string or embed code HTML
 * @returns Canonical URL string, or null if extraction fails
 * 
 * @example
 * extractCanonicalUrl('https://youtube.com/watch?v=123')
 * // 'https://youtube.com/watch?v=123' (normalized)
 * 
 * extractCanonicalUrl('<iframe src="https://youtube.com/embed/123"></iframe>')
 * // 'https://youtube.com/embed/123'
 */
export function extractCanonicalUrl(input: string): string | null {
  try {
    // If it's already a URL, normalize it
    if (input.startsWith('http')) {
      return normalizeUrl(input);
    }
    
    // Try to extract URL from embed code
    const urlMatch = input.match(/https?:\/\/[^\s"'<>]+/);
    if (urlMatch) {
      return normalizeUrl(urlMatch[0]);
    }
    
    return null;
  } catch {
    return null;
  }
}

// Removed unused exports in Phase 4 cleanup:
// - isTrustedUrl
// - classifyInput
// - detectPlatform (note: local function with same name exists in LiveAssets.tsx)
// - detectContentType
// (function bodies removed - were unused)

/**
 * Check if a URL is from a trusted domain
 * 
 * Validates that the URL belongs to a trusted platform domain.
 * Used for security validation before embedding content.
 * 
 * @param url URL string to check
 * @returns true if URL is from a trusted domain, false otherwise
 * 
 * @example
 * isTrustedUrl('https://youtube.com/watch?v=123') // true
 * isTrustedUrl('https://malicious-site.com') // false
 */
export function isTrustedUrl(url: string): boolean {
  const trustedDomains = [
    'youtube.com', 'youtu.be',
    'instagram.com',
    'twitter.com', 'x.com',
    'tiktok.com',
    'facebook.com',
    'linkedin.com'
  ];
  
  try {
    const urlObj = new URL(url);
    return trustedDomains.some(domain => 
      urlObj.hostname === domain || urlObj.hostname.endsWith(`.${domain}`)
    );
  } catch {
    return false;
  }
}

/**
 * Classify input as embed code or URL
 * 
 * Determines whether the input string is an embed code (HTML) or a URL.
 * 
 * @param input String to classify
 * @returns 'embed' if input contains HTML embed patterns, 'url' otherwise
 * 
 * @example
 * classifyInput('https://youtube.com/watch?v=123') // 'url'
 * classifyInput('<iframe src="..."></iframe>') // 'embed'
 */
export function classifyInput(input: string): 'embed' | 'url' {
  if (input.startsWith('http')) {
    return 'url';
  }
  
  // Check for common embed patterns
  if (input.includes('<iframe') || input.includes('<blockquote') || input.includes('<script')) {
    return 'embed';
  }
  
  return 'url';
}

/**
 * Detect platform from URL or embed code
 * 
 * Identifies the platform (youtube, tiktok, instagram, etc.) from a URL
 * or embed code. Returns 'unknown' for shortened URLs that require expansion.
 * 
 * @param input URL string or embed code
 * @returns Platform identifier string, or null if platform cannot be determined
 * 
 * @example
 * detectPlatform('https://youtube.com/watch?v=123') // 'youtube'
 * detectPlatform('https://tiktok.com/@user/video/123') // 'tiktok'
 * detectPlatform('https://t.co/abc123') // 'unknown' (shortened URL)
 */
export function detectPlatform(input: string): string | null {
  if (!input) return null;
  
  const str = input.toLowerCase().trim();
  
  // Handle shortened URLs first
  if (str.includes('t.co/') || str.includes('bit.ly/') || str.includes('tinyurl.com/')) {
    // For shortened URLs, we can't determine platform without expanding
    // Return 'unknown' and let the calling code handle expansion
    return 'unknown';
  }
  
  // Direct platform detection
  if (str.includes('youtube.com') || str.includes('youtu.be')) return 'youtube';
  if (str.includes('tiktok.com')) return 'tiktok';
  if (str.includes('instagram.com')) return 'instagram';
  if (str.includes('twitter.com') || str.includes('x.com')) return 'twitter';
  if (str.includes('facebook.com') || str.includes('fb.com')) return 'facebook';
  if (str.includes('linkedin.com')) return 'linkedin';
  
  // Check for embed code patterns
  if (str.includes('twitter-tweet')) return 'twitter';
  if (str.includes('tiktok-embed')) return 'tiktok';
  if (str.includes('instagram-media')) return 'instagram';
  if (str.includes('data-instgrm-permalink')) return 'instagram';
  
  return null;
}

/**
 * Detect content type from URL and platform
 * 
 * Determines the specific content type (post, reel, video, story, shorts)
 * based on URL patterns and platform. Used for appropriate rendering and sizing.
 * 
 * @param url URL string to analyze
 * @param platform Platform identifier (youtube, instagram, etc.)
 * @returns Content type identifier
 * 
 * @example
 * detectContentType('https://youtube.com/shorts/123', 'youtube') // 'shorts'
 * detectContentType('https://instagram.com/reel/123', 'instagram') // 'reel'
 * detectContentType('https://instagram.com/p/123', 'instagram') // 'post'
 */
export function detectContentType(url: string, platform: string): 'post' | 'reel' | 'video' | 'story' | 'shorts' | 'text' {
  if (!url || !platform) return 'text';
  
  const normalizedUrl = url.toLowerCase();
  
  switch (platform) {
    case 'youtube':
      return normalizedUrl.includes('/shorts/') ? 'shorts' : 'video';
    case 'tiktok':
      return 'video';
    case 'instagram':
      if (normalizedUrl.includes('/reel/')) return 'reel';
      if (normalizedUrl.includes('/p/')) return 'post';
      if (normalizedUrl.includes('/stories/')) return 'story';
      return 'post';
    case 'facebook':
      if (normalizedUrl.includes('/reel/')) return 'reel';
      if (normalizedUrl.includes('/videos/')) return 'video';
      return 'post';
    case 'twitter':
      return 'post';
    case 'linkedin':
      return 'post';
    default:
      return 'text';
  }
}
